import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { useUpdateInsightsSearchMutation } from "../../services/insights.api";
import { MyInsightsSearch, UpdateInsightsSearchRequest } from "../../types";

type RenameSearchFormData = Omit<
  UpdateInsightsSearchRequest["body"],
  "filters"
>;

const renameSearchSchema: yup.SchemaOf<RenameSearchFormData> = yup.object({
  name: yup.string().required("New search name is required"),
});

type RenameSearchProps = {
  search: MyInsightsSearch;
  isOpen: boolean;
  onClose: () => void;
};

const RenameSearchContent = ({
  search,
  onClose,
}: Omit<RenameSearchProps, "isOpen">): React.JSX.Element => {
  const [updateSearchMutation] = useUpdateInsightsSearchMutation();
  const methods = useForm<RenameSearchFormData>({
    resolver: yupResolver(renameSearchSchema),
    defaultValues: {
      name: "",
    },
  });
  const {
    formState: { errors },
    handleSubmit,
  } = methods;

  const onSubmit: SubmitHandler<RenameSearchFormData> = async (data) => {
    try {
      await updateSearchMutation({ id: search.id, body: data }).unwrap();

      Toast.show({
        title: "Search renamed successfully!",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, `Could not rename search ${search.name}`);
    }

    onClose();
  };

  return (
    <FormProvider {...methods}>
      <Form.Form onSubmit={handleSubmit(onSubmit)}>
        <Dialog.Header>
          <Dialog.Header.Title>Rename search</Dialog.Header.Title>
        </Dialog.Header>
        <Dialog.Body>
          <Dialog.Section>
            <Dialog.Section.Description>
              Enter new name for search <b>{search.name}</b>:
            </Dialog.Section.Description>
            <Dialog.Section.Content>
              <Form.TextField
                name="name"
                label="New search name"
                id="new-search-name"
                isRequired
                errors={errors}
              />
            </Dialog.Section.Content>
          </Dialog.Section>
        </Dialog.Body>
        <Dialog.Footer>
          <Dialog.Footer.Actions>
            <Button id="cancel" color="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button id="rename-search" type="submit" color="primary">
              Rename
            </Button>
          </Dialog.Footer.Actions>
        </Dialog.Footer>
      </Form.Form>
    </FormProvider>
  );
};

const RenameSearch = ({
  isOpen,
  ...props
}: RenameSearchProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <RenameSearchContent {...props} />
  </Dialog.Frame>
);

export default RenameSearch;
